import React from 'react';
import { Link } from 'react-router-dom';
import { AppLogo } from '../AppLogo';
import '../../pages/landing.css';

type MarketingLayoutProps = {
  children: React.ReactNode;
  /** Optional nav slot rendered in the header nav area (desktop). Domyślnie kanoniczna nawigacja do podstron treści. */
  nav?: React.ReactNode;
};

/** Kanoniczna nawigacja marketingowa — linki do podstron treści (SEO + UX). */
export const MARKETING_NAV: [string, string][] = [
  ['Funkcje', '/funkcje'],
  ['Zastosowania', '/zastosowania'],
  ['Cennik', '/cennik'],
  ['Blog', '/blog'],
];

function MarketingNavLinks() {
  return (
    <>
      {MARKETING_NAV.map(([label, to]) => (
        <Link
          key={to}
          to={to}
          className="bp-nav-link font-['IBM_Plex_Mono'] text-[#3a414e] no-underline"
          style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.08em' }}
        >
          {label}
        </Link>
      ))}
    </>
  );
}

/** Pozycje panelu mobilnego: nawigacja + konto (poniżej 900 px pasek ich nie mieści). */
const MOBILE_MENU: [string, string][] = [
  ...MARKETING_NAV,
  ['Logowanie', '/login'],
  ['Wczesny dostęp', '/request-access'],
];

export function MarketingLayout({ children, nav }: MarketingLayoutProps) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const headerRef = React.useRef<HTMLElement>(null);
  const menuBtnRef = React.useRef<HTMLButtonElement>(null);
  const menuPanelRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    if (!menuOpen) return;
    // Fokus wchodzi do panelu; Escape oddaje go przyciskowi, który panel otworzył.
    menuPanelRef.current?.querySelector('a')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      menuBtnRef.current?.focus();
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!headerRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [menuOpen]);

  return (
    <div
      className="bp-bg font-['IBM_Plex_Sans'] text-[#14181f]"
      style={{ lineHeight: 1.6, WebkitFontSmoothing: 'antialiased' }}
    >
      {/* ── Ruler top ── */}
      <div className="bp-ruler" aria-hidden="true" />

      {/* ── HEADER ── */}
      <header ref={headerRef} className="bp-header">
        <div className="bp-header-inner mx-auto max-w-[1180px] px-7">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-3 no-underline">
            <span
              className="flex items-center justify-center text-white"
              style={{
                width: 42,
                height: 42,
                border: '1.5px solid #14181f',
                background: '#14181f',
                position: 'relative',
              }}
            >
              <AppLogo className="h-5 w-5" />
            </span>
            <span>
              <strong
                className="block font-['Bricolage_Grotesque'] font-[800] text-[#14181f]"
                style={{ fontSize: 20, letterSpacing: '0.02em', lineHeight: 1 }}
              >
                PINVEO
              </strong>
              <small
                className="bp-brand-sub block font-['IBM_Plex_Mono'] text-[#6b7280]"
                style={{
                  fontSize: 11,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  marginTop: 3,
                }}
              >
                dokumentacja terenowa
              </small>
            </span>
          </Link>

          {/* Nav links slot — domyślnie kanoniczna nawigacja do podstron */}
          <nav
            className="bp-nav-links flex items-center gap-7"
            aria-label="Nawigacja główna"
          >
            {nav ?? <MarketingNavLinks />}
          </nav>

          {/* CTA */}
          <div className="flex items-center gap-2.5">
            <Link
              to="/login"
              className="bp-btn bp-header-login font-['IBM_Plex_Mono'] font-[600] text-[#14181f]"
              style={{
                fontSize: 13,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                padding: '10px 16px',
                border: '1.5px solid #14181f',
                background: 'transparent',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              Logowanie
            </Link>
            <Link
              to="/request-access"
              className="bp-btn bp-btn-hi font-['IBM_Plex_Mono'] font-[600] text-white"
              style={{
                fontSize: 13,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                padding: '10px 16px',
                border: '1.5px solid #ff5a1f',
                background: '#ff5a1f',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              Wczesny dostęp <span aria-hidden="true">→</span>
            </Link>
            <button
              ref={menuBtnRef}
              type="button"
              className="bp-menu-btn"
              aria-label={menuOpen ? 'Zamknij menu' : 'Otwórz menu'}
              aria-expanded={menuOpen}
              aria-controls="bp-mobile-menu"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="square"
                style={{ width: 20, height: 20 }}
              >
                {menuOpen ? (
                  <path d="M6 6l12 12M18 6L6 18" />
                ) : (
                  <path d="M4 7h16M4 12h16M4 17h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav ref={menuPanelRef} id="bp-mobile-menu" className="bp-menu-panel" aria-label="Menu mobilne">
            {MOBILE_MENU.map(([label, to]) => (
              <Link key={to} to={to} onClick={() => setMenuOpen(false)}>
                {label} <span aria-hidden="true">→</span>
              </Link>
            ))}
          </nav>
        )}
      </header>

      {/* ── PAGE CONTENT ── */}
      {children}

      {/* ── Ruler before footer ── */}
      <div className="bp-ruler" aria-hidden="true" />

      {/* ── FOOTER ── */}
      <footer
        className="relative z-[1]"
        style={{ padding: '52px 0 36px', borderTop: '1px solid #cdbfa6' }}
      >
        <div className="mx-auto max-w-[1180px] px-7">
          {/* Brand line */}
          <p
            style={{
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 12,
              color: '#6b7280',
              marginBottom: 36,
            }}
          >
            PINVEO — dokumentacja terenowa, raporty i praca offline.
          </p>

          {/* Sitemap grid */}
          <div
            className="grid gap-8 sm:grid-cols-2 lg:grid-cols-5"
            style={{
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 12.5,
            }}
          >
            {/* Produkt */}
            <div>
              <p
                style={{
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.16em',
                  color: '#14181f',
                  fontWeight: 600,
                  marginBottom: 14,
                }}
              >
                Produkt
              </p>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {([
                  ['Funkcje', '/funkcje'],
                  ['Cennik', '/cennik'],
                ] as [string, string][]).map(([label, to]) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="text-[#3a414e] no-underline transition hover:text-[#e2410a]"
                      style={{ fontSize: 12.5 }}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Zastosowania */}
            <div>
              <p
                style={{
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.16em',
                  color: '#14181f',
                  fontWeight: 600,
                  marginBottom: 14,
                }}
              >
                Zastosowania
              </p>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {([
                  ['Przeglądy okresowe budynków', '/zastosowania/przeglady-okresowe-budynkow'],
                  ['Odbiory mieszkań', '/zastosowania/odbiory-mieszkan'],
                  ['Odbiory domów', '/zastosowania/odbiory-domow'],
                  ['Inwentaryzacja usterek', '/zastosowania/inwentaryzacja-usterek'],
                  ['Inwentaryzacja obiektów', '/zastosowania/inwentaryzacja-obiektow'],
                  ['BHP i audyty', '/zastosowania/bhp-audyty'],
                  ['Instalacje i geodezja', '/zastosowania/instalacje-geodezja'],
                  ['Instalacje elektryczne', '/zastosowania/przeglady-instalacji-elektrycznej'],
                  ['Przewody kominowe i wentylacja', '/zastosowania/kontrola-przewodow-kominowych'],
                  ['Place zabaw', '/zastosowania/przeglady-placow-zabaw'],
                  ['Elewacje i dachy', '/zastosowania/przeglady-elewacji-dachu'],
                  ['Utrzymanie ruchu', '/zastosowania/utrzymanie-ruchu-przeglady'],
                  ['Zarządzanie nieruchomościami', '/zastosowania/zarzadzanie-nieruchomosciami'],
                  ['Wszystkie zastosowania', '/zastosowania'],
                ] as [string, string][]).map(([label, to]) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="text-[#3a414e] no-underline transition hover:text-[#e2410a]"
                      style={{ fontSize: 12.5 }}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Baza wiedzy */}
            <div>
              <p
                style={{
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.16em',
                  color: '#14181f',
                  fontWeight: 600,
                  marginBottom: 14,
                }}
              >
                Baza wiedzy
              </p>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {([
                  ['Blog — wszystkie artykuły', '/blog'],
                  ['Przeglądy okresowe — terminy i kary', '/blog/przeglady-okresowe-budynku-terminy-kary'],
                  ['Książka obiektu budowlanego', '/blog/ksiazka-obiektu-budowlanego-co-zawiera'],
                  ['Przegląd roczny krok po kroku', '/blog/przeglad-roczny-budynku-krok-po-kroku'],
                  ['Przegląd techniczny budynku', '/blog/przeglad-techniczny-budynku-krok-po-kroku'],
                  ['Lista kontrolna BHP na budowie', '/blog/lista-kontrolna-bhp-na-budowie'],
                  ['Protokół odbioru mieszkania', '/blog/protokol-odbioru-mieszkania-co-zawiera'],
                  ['Najczęstsze usterki przy odbiorze', '/blog/najczestsze-usterki-odbior-mieszkania'],
                  ['Snagging — lista usterek', '/blog/snagging-co-to-jest-lista-usterek'],
                  ['Dokumentacja fotograficzna', '/blog/dokumentacja-fotograficzna-na-budowie'],
                  ['Inwentaryzacja powykonawcza', '/blog/inwentaryzacja-powykonawcza-co-zawiera'],
                ] as [string, string][]).map(([label, to]) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="text-[#3a414e] no-underline transition hover:text-[#e2410a]"
                      style={{ fontSize: 12.5 }}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Konto */}
            <div>
              <p
                style={{
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.16em',
                  color: '#14181f',
                  fontWeight: 600,
                  marginBottom: 14,
                }}
              >
                Konto
              </p>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {([
                  ['Logowanie', '/login'],
                  ['Poproś o dostęp', '/request-access'],
                ] as [string, string][]).map(([label, to]) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="text-[#3a414e] no-underline transition hover:text-[#e2410a]"
                      style={{ fontSize: 12.5 }}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Prawne */}
            <div>
              <p
                style={{
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.16em',
                  color: '#14181f',
                  fontWeight: 600,
                  marginBottom: 14,
                }}
              >
                Prawne
              </p>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {([
                  ['Prywatność', '/privacy'],
                  ['Regulamin', '/terms'],
                  ['Usunięcie konta', '/delete-account'],
                ] as [string, string][]).map(([label, to]) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="text-[#3a414e] no-underline transition hover:text-[#e2410a]"
                      style={{ fontSize: 12.5 }}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Hairline + copyright */}
          <div
            aria-hidden="true"
            style={{ height: 1, background: '#cdbfa6', margin: '36px 0 20px' }}
          />
          <p
            style={{
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 11,
              color: '#6b7280',
            }}
          >
            © {new Date().getFullYear()} Pinveo
          </p>
        </div>
      </footer>
    </div>
  );
}
